import { useState } from "react";
import { usePlant } from "@/lib/context";
import api from "@/lib/api";
import { num, inrFull, COLORS } from "@/lib/format";
import { Sparkles, Brain, RefreshCw, TrendingUp } from "lucide-react";

export default function AIAdvisor() {
  const { state, refresh } = usePlant();
  const [whatIf, setWhatIf] = useState({
    target_tap_to_tap_min: 28,
    target_cqs: 85,
    target_yield_pct: 78,
    planned_production_mt: 1000,
  });
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [whatIfBusy, setWhatIfBusy] = useState(false);
  const [narrating, setNarrating] = useState(false);

  if (!state) return <div className="p-10 text-[#8F93A2]">Loading…</div>;

  const ai = state.ai || {};
  const m1 = ai.model1_sec || {};
  const m2 = ai.model2_t2t || {};
  const m4 = ai.model4_rca || {};
  const m5 = ai.model5_yield || {};

  const runWhatIf = async () => {
    setWhatIfBusy(true);
    try {
      const r = await api.post("/ai/whatif", whatIf);
      setWhatIfResult(r.data);
    } finally { setWhatIfBusy(false); }
  };

  const forceNarrative = async () => {
    setNarrating(true);
    try {
      await api.post("/ai/narrate");
      refresh();
    } finally { setNarrating(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-small">AI Advisor</div>
          <h1 className="font-display text-3xl uppercase tracking-tight font-bold">Physics + AI · One Screen</h1>
        </div>
        <button className="btn flex items-center gap-2" onClick={forceNarrative} disabled={narrating} data-testid="refresh-narrative-btn">
          <RefreshCw size={14} className={narrating ? "animate-spin" : ""} /> Refresh Narrative
        </button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span><Brain className="inline mr-1" size={14} /> Model 1 — Next Shift SEC Prediction</span></div>
          <div className="p-5">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="label-small">Predicted SEC</div>
                <div className="metric-mid text-[#00E5FF]" data-testid="m1-predicted">{num(m1.predicted_sec, 0)}</div>
                <div className="text-[10px] text-[#8F93A2]">kWh/tonne</div>
              </div>
              <div>
                <div className="label-small">CI 90%</div>
                <div className="font-mono text-sm mt-2">{num(m1.ci_low, 0)} – {num(m1.ci_high, 0)}</div>
              </div>
              <div>
                <div className="label-small">Shift Cost</div>
                <div className="metric-mid">{inrFull(m1.predicted_shift_cost_inr)}</div>
              </div>
            </div>
            <div className="mt-4 text-xs text-[#8F93A2] leading-relaxed">{m1.label}</div>
            <div className="mt-3 p-3 bg-[#14151C] border-l-2 border-[#00E5FF] text-sm">
              Recommended minimum charge quality target: <span className="font-mono text-[#00FF66]">CQS ≥ {m1.min_cqs_target}</span>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span><Sparkles className="inline mr-1" size={14} /> AI Narrative · Llama 3 (AWS Bedrock)</span></div>
          <div className="p-5">
            <div className="text-sm text-white/90 leading-relaxed" data-testid="advisor-narrative">{state.narrative}</div>
            <div className="mt-3 text-[10px] text-[#8F93A2]">Generated from deterministic Model 4 RCA output. Rule-based scoring is the source of truth.</div>
          </div>
        </div>

        <div className="col-span-12 panel">
          <div className="panel-header"><span>Active Root Cause Analysis — Model 4</span></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="m4-table">
              <thead className="text-[#8F93A2] uppercase tracking-wider text-[10px]">
                <tr className="border-b border-[#1F212A]">
                  <th className="text-left p-3">Rank</th>
                  <th className="text-left p-3">Cause</th>
                  <th className="text-right p-3">Probability</th>
                  <th className="text-right p-3">Impact kWh/t</th>
                  <th className="text-right p-3">₹/shift</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Role</th>
                </tr>
              </thead>
              <tbody>
                {(m4.causes || []).map((c, i) => (
                  <tr key={i} className="border-b border-[#14151C]" data-testid={`m4-row-${i}`}>
                    <td className="p-3 font-mono">#{i + 1}</td>
                    <td className="p-3">{c.cause}</td>
                    <td className="p-3 text-right font-mono text-[#00E5FF]">{num(c.probability, 0)}%</td>
                    <td className="p-3 text-right font-mono">{num(c.impact_kwh_per_t, 1)}</td>
                    <td className="p-3 text-right font-mono text-[#FF3333]">{inrFull(c.impact_inr_per_shift)}</td>
                    <td className="p-3 text-xs">{c.action}</td>
                    <td className="p-3 text-xs text-[#00FF66]">{c.action_role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-span-12 panel">
          <div className="panel-header"><span>Ranked Recommendations · all active</span></div>
          <div className="divide-y divide-[#14151C]">
            {(state.recommendations || []).map((r) => (
              <div key={r.id} className="p-4 grid grid-cols-12 gap-3 items-center" data-testid={`rec-${r.id}`}>
                <div className="col-span-1 font-mono text-[#00E5FF]">#{r.priority}</div>
                <div className="col-span-5">
                  <div className="font-display uppercase tracking-wider text-sm font-semibold">{r.cause}</div>
                  <div className="text-xs text-[#8F93A2] mt-1">{r.action}</div>
                </div>
                <div className="col-span-2 text-center">
                  <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider">Probability</div>
                  <div className="font-mono">{num(r.probability, 0)}%</div>
                </div>
                <div className="col-span-2 text-center">
                  <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider">Annual saving</div>
                  <div className="font-mono text-[#00FF66]">{inrFull(r.estimated_saving_inr_year)}</div>
                </div>
                <div className="col-span-1 text-center">
                  <span className={`badge-status ${r.effort === "immediate" ? "green" : r.effort === "scheduled" ? "yellow" : "red"}`}>
                    {r.effort}
                  </span>
                </div>
                <div className="col-span-1 text-right text-xs text-[#00FF66]">{r.role}</div>
              </div>
            ))}
            {!(state.recommendations || []).length && (
              <div className="p-6 text-center text-sm text-[#8F93A2]">No recommendations — plant operating within parameters.</div>
            )}
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 panel p-5" data-testid="m2-pattern">
          <div className="label-small">Model 2 — Tap-to-Tap Pattern</div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider">Baseline</div>
              <div className="metric-mid">{num(m2.baseline_t2t_min, 1)}<span className="text-xs ml-1">min</span></div>
            </div>
            <div>
              <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider">Current avg</div>
              <div className="metric-mid text-[#00E5FF]">{num(m2.current_avg_t2t_min, 1)}<span className="text-xs ml-1">min</span></div>
            </div>
            <div>
              <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider">Drift</div>
              <div className={`metric-mid ${m2.drift_min > 5 ? "text-[#FF3333]" : "text-[#00FF66]"}`}>{m2.drift_min > 0 ? "+" : ""}{num(m2.drift_min, 1)}</div>
            </div>
            <div>
              <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider">Classification</div>
              <div className="font-display uppercase tracking-wider text-sm mt-2">{m2.classification || "—"}</div>
            </div>
          </div>
          <div className="mt-3 text-[11px] text-[#8F93A2]">{m2.label}</div>
        </div>

        <div className="col-span-12 md:col-span-6 panel p-5" data-testid="m5-yield">
          <div className="label-small">Model 5 — Yield Forecast (next 2h)</div>
          <div className="mt-3 flex items-baseline gap-3">
            <div className="metric-large text-[#00E5FF]">{num(m5.forecast_2h_pct, 1)}<span className="text-sm">%</span></div>
            <div className="text-xs text-[#8F93A2]">current: {num(m5.current_yield_pct, 1)}%</div>
          </div>
          <div className="mt-3 text-xs text-[#8F93A2] leading-relaxed">{m5.label}</div>
        </div>

        <div className="col-span-12 panel">
          <div className="panel-header"><span><TrendingUp className="inline mr-1" size={14} /> What-If Simulator · Physics Only</span></div>
          <div className="p-5 grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6 space-y-4">
              <SliderRow label="Target tap-to-tap (min)" min={22} max={50} step={1} value={whatIf.target_tap_to_tap_min}
                         testid="whatif-t2t"
                         onChange={(v) => setWhatIf({ ...whatIf, target_tap_to_tap_min: v })} />
              <SliderRow label="Target CQS" min={50} max={100} step={1} value={whatIf.target_cqs}
                         testid="whatif-cqs"
                         onChange={(v) => setWhatIf({ ...whatIf, target_cqs: v })} />
              <SliderRow label="Target yield %" min={60} max={85} step={1} value={whatIf.target_yield_pct}
                         testid="whatif-yield"
                         onChange={(v) => setWhatIf({ ...whatIf, target_yield_pct: v })} />
              <SliderRow label="Planned production MT/month" min={500} max={2000} step={50} value={whatIf.planned_production_mt}
                         testid="whatif-prod"
                         onChange={(v) => setWhatIf({ ...whatIf, planned_production_mt: v })} />
              <button className="btn primary w-full" onClick={runWhatIf} disabled={whatIfBusy} data-testid="whatif-run-btn">
                {whatIfBusy ? "Simulating…" : "Run Simulation"}
              </button>
            </div>
            <div className="col-span-12 md:col-span-6">
              {whatIfResult ? (
                <div className="space-y-3" data-testid="whatif-result">
                  <Result label="Predicted SEC" value={`${num(whatIfResult.predicted_sec, 0)} kWh/t`} color={COLORS.blue} />
                  <Result label="Monthly kWh" value={num(whatIfResult.monthly_kwh, 0).toLocaleString()} />
                  <Result label="Monthly cost" value={inrFull(whatIfResult.monthly_cost_inr)} />
                  <Result label="Baseline monthly cost" value={inrFull(whatIfResult.baseline_monthly_cost_inr)} color={COLORS.textDim} />
                  <div className="border-t border-[#1F212A] pt-3">
                    <Result label="Annual saving" value={inrFull(whatIfResult.annual_saving_inr)} color={COLORS.green} big />
                  </div>
                  <div className="mt-4 text-[11px] text-[#8F93A2]">Physics simulation — adjust inputs to model improvement scenarios. No AI dependency.</div>
                </div>
              ) : (
                <div className="text-sm text-[#8F93A2] p-4">
                  Adjust the sliders and click <span className="text-[#00E5FF]">Run Simulation</span> to model
                  how operational changes translate into monthly rupee savings using only the L3 physics engine.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SliderRow({ label, min, max, step, value, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="label-small">{label}</span>
        <span className="font-mono text-[#00E5FF]">{value}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
             onChange={(e) => onChange(parseFloat(e.target.value))}
             className="w-full" data-testid={testid} />
    </div>
  );
}

function Result({ label, value, color, big }) {
  return (
    <div className="flex items-center justify-between border-b border-[#14151C] py-2">
      <span className="label-small">{label}</span>
      <span className={big ? "metric-mid" : "font-mono"} style={{ color: color || COLORS.text }}>{value}</span>
    </div>
  );
}
